/**
 * @file     listingMediaLoader.ts
 * @purpose  Client-side request-coalescing loader for listing media. Collapses a
 *           burst of per-card / per-pin media requests into batched
 *           GET /api/mls/media-list calls (DataLoader pattern), so a property
 *           grid spends ONE shared 60/min API-bucket token instead of ~30 and
 *           stops 429-ing the rest of /api. Results are cached per session.
 */

export type ListingMedia = { mediaKey: string; mediaOrder: number | null };

const BATCH_DEBOUNCE_MS = 40;
const MAX_BATCH = 30; // must not exceed the route's MAX_KEYS

const cache = new Map<string, ListingMedia[]>();
const waiters = new Map<string, ((media: ListingMedia[]) => void)[]>();
let queued: string[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

/** Resolve the media list for one listing, batched across concurrent callers. */
export function loadListingMedia(listingKey: string): Promise<ListingMedia[]> {
  const cached = cache.get(listingKey);
  if (cached) return Promise.resolve(cached);
  return new Promise<ListingMedia[]>((resolve) => {
    const arr = waiters.get(listingKey) ?? [];
    arr.push(resolve);
    waiters.set(listingKey, arr);
    if (!queued.includes(listingKey)) queued.push(listingKey);
    schedule();
  });
}

function schedule(): void {
  if (queued.length >= MAX_BATCH) {
    void flush();
    return;
  }
  if (timer) return;
  timer = setTimeout(() => void flush(), BATCH_DEBOUNCE_MS);
}

async function flush(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const batch = queued.slice(0, MAX_BATCH);
  queued = queued.slice(MAX_BATCH);
  if (batch.length === 0) return;

  let byKey: Record<string, ListingMedia[]> = {};
  try {
    const res = await fetch(`/api/mls/media-list?keys=${batch.map(encodeURIComponent).join(',')}`);
    if (res.ok) byKey = (await res.json()) as Record<string, ListingMedia[]>;
  } catch {
    // Network error → resolve to empty; callers fall back to the primary image.
  }

  for (const key of batch) {
    const media = byKey[key] ?? [];
    cache.set(key, media);
    const resolvers = waiters.get(key);
    waiters.delete(key);
    resolvers?.forEach((resolve) => resolve(media));
  }

  // Drain any overflow beyond MAX_BATCH.
  if (queued.length > 0) schedule();
}
